import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_ENDPOINTS } from '../config/apiConfig';
import { useAuth } from '../components/router/authContext';
import {
  FaUser, FaBell, FaShieldAlt, FaBox, FaMoneyCheckAlt,
  FaCcVisa, FaCcMastercard, FaCcAmex, FaCcDiscover, FaCcDinersClub, FaCcJcb,
} from 'react-icons/fa';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { LoadingSpinner } from "../components/general/LoadingSpinner";

function Profile() {
  const [collapsed, setCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  const { logout, refreshUserData, currentUser } = useAuth();
  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");

  const [contentRef, isContentVisible] = useScrollAnimation({ triggerOnce: true, delay: 150 });

  // Use currentUser from AuthContext instead of local userData
  const userData = currentUser;

  const cancellationConfirmationWindow = () => {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999] animate-fade-in font-nunito"
        style={{ margin: 0, padding: 0 }}
        onClick={(e) => {
          // Close modal if clicking on backdrop
          if (e.target === e.currentTarget) {
            setShowCancelConfirmation(false);
          }
        }}
      >
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md mx-4 transform transition-all duration-300 ease-out animate-scale-in">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Cancel Subscription</h2>
          <p className="text-gray-600 mb-6">Are you sure you want to cancel your subscription? This action cannot be undone.</p>
          <div className="flex gap-4 justify-end">
            <button
              className="px-6 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              onClick={() => setShowCancelConfirmation(false)}
            >
              Never mind
            </button>
            <button
              className="px-6 py-2 font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              onClick={() => {
                handleCancelSubscription();
                setShowCancelConfirmation(false);
              }}
            >
              Yes, I'm sure
            </button>
          </div>
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (currentUser) {
      setFormData(currentUser);
      setLoading(false);
    } else if (!currentUser && !loading) {
      // If no current user and not loading, redirect to login
      navigate('/login');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (showCancelConfirmation) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCancelConfirmation]);

  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        notification_preferences: { ...prev.notification_preferences, [name]: checked }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    axios.post(API_ENDPOINTS.updateProfile, formData, {
      headers: { Authorization: `Token ${authToken}` },
    })
      .then(response => {
        refreshUserData(); // Refresh user data from AuthContext
        setSuccessMessage('Profile updated successfully!');
        setIsEditing(false);
      })
      .catch(error => {
        setErrorMessage('Failed to update profile. Please try again.');
        console.error(error);
      })
      .finally(() => setLoading(false));
  };

  const handleCancel = () => {
    setFormData(currentUser);
    setIsEditing(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleCancelSubscription = () => {
    if (currentUser.stripe_customer_id && currentUser.subscription_status === 'active') {
      axios.post(API_ENDPOINTS.cancelSubscription, {}, {
        headers: { Authorization: `Token ${authToken}` }
      })
        .then(response => {
          setSuccessMessage('Subscription cancelled successfully!');
          refreshUserData(); // Refresh user data to update the UI
        })
        .catch(error => {
          setErrorMessage('Failed to cancel subscription. Please try again.');
          console.error(error);
        });
    }
    else {
      setErrorMessage('You do not have an active subscription.');
    }
  };

  const handleResendVerifyEmail = () => {
    axios.post(API_ENDPOINTS.resendVerificationEmail, {}, {
      headers: { Authorization: `Token ${authToken}` }
    })
      .then(response => {
        setSuccessMessage('Please check your email for a verification link.');
      })
      .catch(error => {
        setErrorMessage('Failed to send verification email. Please try again.');
        console.error(error);
      });
  };

  const handleSyncWithStripe = () => {
    axios.post(API_ENDPOINTS.syncWithStripe, {}, {
      headers: { Authorization: `Token ${authToken}` }
    })
      .then(response => {
        setSuccessMessage('Synced with Stripe successfully!');
      })
      .catch(error => {
        setErrorMessage('Failed to sync with Stripe. Please try again.');
        console.error(error);
      });
  };

  const InputField = ({ id, label, value, ...props }) => (
    <div>
      <label htmlFor={id} className="block mb-2 text-sm font-medium text-gray-700">{label}</label>
      <input
        id={id}
        name={id}
        value={value || ''}
        onChange={handleInputChange}
        disabled={!isEditing}
        className="w-full px-3 py-2 bg-gray-100 border-transparent rounded-md focus:ring-blue-500 focus:border-blue-500 focus:bg-white disabled:bg-gray-200 disabled:cursor-not-allowed transition"
        {...props}
      />
    </div>
  );

  const FormActions = () => (
    <div className="flex items-center gap-4 mt-6">
      <button type="submit" className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50" disabled={loading}>
        {loading ? 'Saving...' : 'Save Changes'}
      </button>
      <button type="button" className="px-6 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300" onClick={handleCancel}>
        Cancel
      </button>
    </div>
  );

  const Section = ({ title, description, children }) => (
    <div className="bg-white p-8 rounded-xl shadow-lg">
      <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
      <div className="mt-6">{children}</div>
    </div>
  );

  const renderPersonalInfo = () => (
    <Section title="Personal Information" description="">
      <form onSubmit={handleSubmit}>
        <div className="mt-6 space-y-6">
          <InputField id="email" label="Email Address" type="email" value={formData.email} required />
          <InputField id="phone_number" label="Phone Number" type="tel" value={formData.phone_number} />
          <InputField id="company_name" label="Company Name" value={formData.company_name} />
        </div>
        {isEditing ? <FormActions /> : (
          <div className="mt-6 flex gap-4">
            <button type="button" className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700" onClick={() => setIsEditing(true)}>Edit Information</button>
            <button type="button" className="px-6 py-2 font-semibold text-white bg-red-600 rounded-md hover:bg-red-700" onClick={handleLogout}>Logout</button>
          </div>
        )}
      </form>
    </Section>
  );

  const ToggleSwitch = ({ id, label, description, checked }) => (
    <div className="flex items-center justify-between py-4 border-b">
      <div>
        <h3 className="font-medium text-gray-800">{label}</h3>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" id={id} name={id} checked={checked || false} onChange={handleInputChange} disabled={!isEditing} className="sr-only peer" />
        <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      </label>
    </div>
  );

  const renderNotifications = () => (
    <Section title="Notification Preferences" description="">
      <form onSubmit={handleSubmit}>
        <ToggleSwitch id="email_notifications" label="Email Notifications" description="Receive important updates via email" checked={formData.notification_preferences?.email_notifications} />
        <ToggleSwitch id="sms_notifications" label="SMS Notifications" description="Receive important updates via text message" checked={formData.notification_preferences?.sms_notifications} />
        <ToggleSwitch id="weekly_reports" label="Weekly Reports" description="Receive weekly fleet summary reports" checked={formData.notification_preferences?.weekly_reports} />
        <ToggleSwitch id="marketing_emails" label="Marketing Emails" description="Receive new feature and promotion updates" checked={formData.notification_preferences?.marketing_emails} />
        {isEditing ? <FormActions /> : <button type="button" className="mt-6 px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700" onClick={() => setIsEditing(true)}>Edit Preferences</button>}
      </form>
    </Section>
  );

  const renderAccountSettings = () => {
    return (
      <Section title="Account Settings" description="">
        <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
          <ul className="divide-y divide-gray-200">
            <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-gray-600">Username</span><span>{userData.username}</span></li>
            <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-gray-600">Verified Email</span><span>{userData.email_verified ? 'Verified' : 'Unverified'}</span></li>
          </ul>
        </div>
        {!userData.email_verified && (
          <button className="mt-6 px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700" onClick={() => handleResendVerifyEmail(true)}>Resend Verification Email</button>
        )}
      </Section>
    );
  };

  const renderProducts = () => {
    return (
      <Section title="Your Products" description="Manage your products and their details.">
        <div>
          <h2>Your Products</h2>
        </div>
      </Section>
    );
  };

  const renderSubscriptions = () => {
    const StatusBadge = ({ status }) => (
      <span className={`px-2 py-1 text-xs font-semibold leading-tight rounded-full ${status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
        {status}
      </span>
    );
    return (
      <Section title="Manage Subscriptions" description="">
        <div>
          <h2 className="text-lg font-bold text-gray-600 mt-6 py-2">AmpTrans Fleet Planner</h2>
          <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
            <ul className="divide-y divide-gray-200">
              <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-gray-600">Subscription Type</span><span className="font-medium capitalize">{userData.subscription_type}</span></li>
              <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-gray-600">Status</span><StatusBadge status={userData.subscription_status} /></li>
              {
                userData.subscription_status === 'cancelled' ? (
                  <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-gray-600">Expires</span><span>{new Date(userData.subscription_end_date).toLocaleDateString()}</span></li>
                )
                  : (
                    <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-gray-600">Renewal Date</span><span>{userData.subscription_renewal_date ? new Date(userData.subscription_renewal_date).toLocaleDateString() : 'N/A'}</span></li>
                  )
              }
            </ul>
          </div>
          {
            userData.subscription_status === 'active' && (
              <button className="mt-6 px-6 py-2 font-semibold text-white bg-red-600 rounded-md hover:bg-red-700" onClick={() => setShowCancelConfirmation(true)}>Cancel Subscription</button>
            )
          }
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-600 mt-6 py-2">Payment Details</h2>
          <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
            <ul className="divide-y divide-gray-200">
              <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-gray-600">Card Number</span>
                <div className="flex items-center gap-6">
                  <span className="font-medium capitalize">**** **** **** {userData.last4}</span>
                  <span className="font-medium">{userData.card_exp_month}/{userData.card_exp_year % 100}</span>
                  {userData.card_brand === 'visa' && <FaCcVisa className="w-8 h-auto text-blue-700" />}
                  {userData.card_brand === 'mastercard' && <FaCcMastercard className="w-8 h-auto" />}
                  {userData.card_brand === 'amex' && <FaCcAmex className="w-8 h-auto" />}
                  {userData.card_brand === 'discover' && <FaCcDiscover className="w-8 h-auto" />}
                  {userData.card_brand === 'diners' && <FaCcDinersClub className="w-8 h-auto" />}
                  {userData.card_brand === 'jcb' && <FaCcJcb className="w-8 h-auto" />}
                </div>
              </li>
              <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-gray-600">Upcoming Payment Amount</span> <span className="font-medium">{userData.subscription_payment_amount ? `$${userData.subscription_payment_amount}` : '--'}</span>
              </li>
            </ul>
          </div>
        </div>
      </Section>
    );
  };

  const NavItem = ({ id, label, icon }) => (
    <li>
      <button onClick={() => { setActiveSection(id); setIsEditing(false); }} className={`w-full flex items-center gap-3 px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeSection === id ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}>
        {icon}
        <span>{label}</span>
      </button>
    </li>
  );

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'personal': return renderPersonalInfo();
      case 'notifications': return renderNotifications();
      case 'account': return renderAccountSettings();
      case 'products': return renderProducts();
      case 'subscriptions': return renderSubscriptions();
      default: return renderPersonalInfo();
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Modal should be at the root level to cover entire screen */}
      {showCancelConfirmation && cancellationConfirmationWindow()}

      {/* <ProSidebar collapsed={collapsed} toggleCollapse={toggleCollapse} /> */}
      <main className={`flex-1 bg-gray-50 font-nunito`}>
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <div className={`mb-8`}>
            <h1 className="text-3xl font-bold text-gray-900">{userData.first_name} {userData.last_name}</h1>
          </div>

          {successMessage && <div className="mb-6 p-4 text-sm text-green-700 bg-green-100 rounded-lg animate-fade-in">{successMessage}</div>}
          {errorMessage && <div className="mb-6 p-4 text-sm text-red-700 bg-red-100 rounded-lg animate-fade-in">{errorMessage}</div>}

          <div
            ref={contentRef}
            className={`grid grid-cols-1 gap-10 lg:grid-cols-4 transition-all duration-700 ease-out ${isContentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <aside className="lg:col-span-1">
              <nav>
                <ul className="space-y-1">
                  <NavItem id="personal" label="Personal Info" icon={<FaUser />} />
                  <NavItem id="notifications" label="Notifications" icon={<FaBell />} />
                  <NavItem id="account" label="Account" icon={<FaShieldAlt />} />
                </ul>
                <ul className="pt-4">
                  {/* <NavItem id="products" label="Your Products" icon={<FaBox />} /> */}
                  <NavItem id="subscriptions" label="Subscriptions" icon={<FaMoneyCheckAlt />} />
                </ul>
              </nav>
            </aside>

            <div className="lg:col-span-3">
              {renderActiveSection()}
            </div>
          </div>
          {/* For testing purposes only */}
          {/* <button className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700" onClick={() => handleSyncWithStripe()}>Sync with Stripe</button> */}
        </div>
      </main>
    </div>
  );
}

export default Profile;
